import { createFileRoute } from "@tanstack/react-router"
import type { LinkProps } from "@tanstack/react-router"
import { useMemo } from "react"

import TabsLayout from "@/components/common/TabsLayout/TabsLayout"
import PermissionContentWrapper from "@/components/PermissionWrapper/PermissionContentWrapper"
import { PermissionActionType } from "@/constants/permissions"
import { usePermissionManager } from "@/hooks/usePermissionManager"

// `path` is typed against the generated route tree rather than `string`, so a tab pointing at a
// route that doesn't exist is a compile error instead of a 404 at runtime.
interface AccountingTab {
  id: string
  label: string
  path: NonNullable<LinkProps["to"]>
}

export const Route = createFileRoute("/app/accounting/_layout")({
  component: () => (
    <PermissionContentWrapper
      permissions={[
        PermissionActionType.GL_TRANSACTION_VIEW,
        PermissionActionType.ACCOUNTS_PAYABLE_VIEW,
        PermissionActionType.ACCOUNTS_PAYABLE_MAINTENANCE,
        PermissionActionType.PAYABLE_APPROVAL,
        PermissionActionType.PAY_FILE,
      ]}
    >
      <AccountingLayout />
    </PermissionContentWrapper>
  ),
})

export function AccountingLayout(): JSX.Element {
  const { hasPermissions } = usePermissionManager()
  const canSeeGlTransactions = hasPermissions([PermissionActionType.GL_TRANSACTION_VIEW])
  const canSeeAccountsPayable = hasPermissions([
    PermissionActionType.ACCOUNTS_PAYABLE_VIEW,
    PermissionActionType.ACCOUNTS_PAYABLE_MAINTENANCE,
  ])
  const canSeePayableApprovals = hasPermissions([PermissionActionType.PAYABLE_APPROVAL])
  const canSeePayFiles = hasPermissions([PermissionActionType.PAY_FILE])

  const tabs = useMemo<AccountingTab[]>(
    () => [
      ...(canSeeGlTransactions
        ? ([
            { id: "gl-transactions", label: "GL Transactions", path: "/app/accounting/gl-transactions" },
          ] satisfies AccountingTab[])
        : []),
      ...(canSeeAccountsPayable
        ? ([
            { id: "accounts-payable", label: "Accounts Payable", path: "/app/accounting/accounts-payable" },
          ] satisfies AccountingTab[])
        : []),
      ...(canSeePayableApprovals
        ? ([
            { id: "payable-approvals", label: "Payable Approvals", path: "/app/accounting/payable-approvals" },
          ] satisfies AccountingTab[])
        : []),
      ...(canSeePayFiles
        ? ([
            {
              id: "huntington-pay-files",
              label: "Huntington Pay Files",
              path: "/app/accounting/huntington-pay-files",
            },
          ] satisfies AccountingTab[])
        : []),
    ],
    [canSeeGlTransactions, canSeeAccountsPayable, canSeePayableApprovals, canSeePayFiles]
  )

  return <TabsLayout title='Accounting' tabs={tabs} />
}
